(() => {
  if (customElements.get('rn-size-finder')) return;
  const SC = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  const AU = [6, 8, 10, 12, 14, 16, 18, 20, 22];
  const ALIAS = { EXTRASMALL: 'XS', 'X-SMALL': 'XS', XSMALL: 'XS', SMALL: 'S', MEDIUM: 'M', LARGE: 'L', EXTRALARGE: 'XL', 'X-LARGE': 'XL', XLARGE: 'XL', XXL: '2XL', XXXL: '3XL', XXXXL: '4XL', XXXXXL: '5XL' };
  const norm = v => {
    const s = String(v == null ? '' : v).split('(')[0].trim().toUpperCase().replace(/\s+/g, '');
    return SC.includes(s) ? s : (ALIAS[s] || null);
  };
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const ftin = cm => { const t = Math.round(cm / 2.54); return Math.floor(t / 12) + ' ft ' + (t % 12) + ' in'; };
  const label = x => x.key + ' (AU ' + AU[x.idx] + ')';

  /* Body questions per product zone (set by snippets/rn-size-finder.liquid from the product type):
     upper = tops, dresses, jackets: tummy, then chest
     lower = pants, skirts, shorts, denim: thighs only (the drawings show hips and thighs)
     both  = matching sets: chest, then thighs
     none  = shoes, bags, accessories: no body quiz, the size chart only
     The answer key 'hip' is the side-view tummy drawing (flat, average, round). */
  const BODY = { upper: ['hip', 'chest'], lower: ['thigh'], both: ['chest', 'thigh'] };

  /* Line drawings come from rn-size-art.js (window.RN_SF_ART): side = tummy, front = chest, legs = thighs. */
  const art = (kind, key) => {
    const a = (window.RN_SF_ART || {})[kind];
    return a && a.p[key] ? '<svg class="rn-sf__ill rn-sf__ill--' + kind + '" viewBox="' + a.vb + '" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="' + a.p[key] + '"/></svg>' : '';
  };
  const calm = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define('rn-size-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.fit = this.dataset.fit || 'small';
      this.zone = BODY[this.dataset.zone] ? this.dataset.zone : (this.dataset.zone === 'none' ? 'none' : 'upper');
      this.tabs = this.querySelector('[data-sf-tabs]');
      this.quiz = this.querySelector('[data-sf-quiz]');
      this.chart = this.querySelector('[data-sf-chart]');
      if (this.zone === 'none') return; // shoes, bags, accessories: chart only
      this.steps = ['usual', 'diff'].concat(BODY[this.zone], ['height', 'fit']);
      let options = [];
      try { options = JSON.parse(this.querySelector('[data-sf-options]').textContent); } catch (e) { return; }
      const sizeOption = options.find(o => /size|taille/i.test(o.name));
      this.avail = [];
      ((sizeOption && sizeOption.values) || []).forEach(v => {
        const key = norm(typeof v === 'string' ? v : v.name);
        if (key && SC.indexOf(key) >= 0) this.avail.push({ key, idx: SC.indexOf(key) });
      });
      if (this.avail.length < 2) return; // chart only
      this.tabs.hidden = false;
      this.tabs.addEventListener('click', e => { const b = e.target.closest('[data-sf-tab]'); if (b) this.show(b.dataset.sfTab); });
      this.tabs.addEventListener('keydown', e => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const next = this.quiz.hidden ? 'quiz' : 'chart';
        this.show(next); this.tabs.querySelector('[data-sf-tab="' + next + '"]').focus();
      });
      this.quiz.addEventListener('click', e => this.onClick(e));
      this.quiz.addEventListener('input', e => {
        if (!e.target.matches('[data-sf-height]')) return;
        this.ans.height = Number(e.target.value);
        this.quiz.querySelector('[data-sf-hv]').textContent = this.ans.height + ' cm';
        this.quiz.querySelector('[data-sf-hs]').textContent = ftin(this.ans.height);
      });
      const dialog = this.closest('dialog');
      if (dialog) dialog.addEventListener('close', () => this.reset());
      this.reset();
    }

    reset() {
      this.ans = { usual: null, diff: 0, hip: 'average', chest: 'average', thigh: 'average', height: 165, fit: 'regular' };
      this.step = 0; this.rec = null; this.busy = false; this.seen = {};
      this.mark(null);
      this.show('quiz');
      this.draw();
    }

    show(which) {
      this.quiz.hidden = which !== 'quiz';
      this.chart.hidden = which !== 'chart';
      this.tabs.querySelectorAll('[data-sf-tab]').forEach(b => {
        const on = b.dataset.sfTab === which;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
    }

    draw() {
      const s = this.step, total = this.steps.length, name = this.steps[s]; let h = '';
      const q = t => '<h3 class="rn-sf__q">' + t + '</h3>';
      const back = s > 0 ? '<button type="button" class="rn-sf__back" data-sf-back aria-label="Back">&#8592;</button>' : '';
      const row = (a, v, t, sub) => '<button type="button" class="rn-sf__opt" data-a="' + a + '" data-v="' + v + '">' + t + (sub ? '<small>' + sub + '</small>' : '') + '</button>';
      const pic = (a, v, svg, t) => '<button type="button" class="rn-sf__pic" data-a="' + a + '" data-v="' + v + '">' + svg + '<span>' + t + '</span></button>';
      if (name === 'usual') {
        h = q('What size do you usually wear?') + '<div class="rn-sf__grid">' +
          this.avail.slice().sort((x, y) => x.idx - y.idx).map(x => row('usual', x.key, label(x))).join('') + '</div>';
      } else if (name === 'diff') {
        h = q('Do you always wear this size?') + '<div class="rn-sf__stack">' +
          row('diff', -1, 'No, I sometimes wear a smaller size') + row('diff', 0, 'Yes, always this size') + row('diff', 1, 'No, I sometimes wear a bigger size') + '</div>';
      } else if (name === 'hip') {
        h = q('How would you describe your tummy?') + '<div class="rn-sf__pics">' +
          pic('hip', 'flat', art('side', 'flat'), 'Flat') + pic('hip', 'average', art('side', 'average'), 'Average') + pic('hip', 'round', art('side', 'round'), 'Round') + '</div>';
      } else if (name === 'thigh') {
        h = q('How would you describe your thighs?') + '<div class="rn-sf__pics">' +
          pic('thigh', 'slim', art('legs', 'slim'), 'Slim') + pic('thigh', 'average', art('legs', 'average'), 'Average') + pic('thigh', 'full', art('legs', 'full'), 'Full') + '</div>';
      } else if (name === 'chest') {
        h = q('How would you describe your chest?') + '<div class="rn-sf__pics">' +
          pic('chest', 'slim', art('front', 'slim'), 'Slim') + pic('chest', 'average', art('front', 'average'), 'Average') + pic('chest', 'broad', art('front', 'broad'), 'Broad') + '</div>';
      } else if (name === 'height') {
        h = q('How tall are you?') + '<div class="rn-sf__height"><div class="rn-sf__height-big" data-sf-hv>' + this.ans.height + ' cm</div>' +
          '<div class="rn-sf__height-sub" data-sf-hs>' + ftin(this.ans.height) + '</div>' +
          '<div class="rn-sf__range"><input type="range" min="145" max="190" value="' + this.ans.height + '" data-sf-height aria-label="Height in centimetres"></div>' +
          '<button type="button" class="rn-sf__btn" data-sf-next>Next</button></div>';
      } else if (name === 'fit') {
        h = q('What fit would you prefer for this item?') + '<div class="rn-sf__stack">' +
          row('fit', 'slim', 'Slim fit', 'close to your usual size') + row('fit', 'regular', 'Regular fit', 'recommended') + row('fit', 'loose', 'Loose fit', 'one size up') + '</div>';
      } else {
        const r = this.rec;
        h = '<div class="rn-sf__result"><p class="rn-sf__lead">Your size for this item</p>' +
          '<p class="rn-sf__sub">Based on the size you usually wear and your answers.</p>' +
          '<p class="rn-sf__mini">Your size</p><div class="rn-sf__size">' + label(r) + '</div>' +
          (r.notes.length ? '<div class="rn-sf__notes">' + r.notes.map(t => '<p>' + t + '</p>').join('') + '</div>' : '') +
          '<button type="button" class="rn-sf__btn" data-sf-choose>Choose this size</button>' +
          '<button type="button" class="rn-sf__link" data-sf-redo>Redo the quiz</button></div>';
      }
      const foot = s < total ? 'Step ' + (s + 1) + ' / ' + total : 'Done';
      this.quiz.innerHTML = '<div class="rn-sf__stage">' + back + h + '<p class="rn-sf__foot">' + foot + '</p></div>';
      if (s < total) this.quiz.querySelectorAll('[data-a]').forEach(b => { if (this.seen[b.dataset.a] && String(this.ans[b.dataset.a]) === b.dataset.v) b.classList.add('is-picked'); });
      const first = this.quiz.querySelector('button.rn-sf__opt,button.rn-sf__pic,button.rn-sf__btn');
      if (first && name !== 'height') { try { first.focus({ preventScroll: true }); } catch (e) {} }
    }

    compute() {
      const a = this.ans, z = this.zone, u = SC.indexOf(a.usual), notes = [];
      const chestAdj = z === 'lower' ? 0 : { slim: -0.5, average: 0, broad: 0.5 }[a.chest];
      const hipAdj = { flat: 0, average: 0, round: 0.5 }[a.hip];
      const thighAdj = (z === 'lower' || z === 'both') ? { slim: 0, average: 0, full: 0.5 }[a.thigh] : 0;
      let fitAdd = 0;
      if (this.fit === 'small') fitAdd = a.fit === 'slim' ? 0 : 1;
      else if (this.fit === 'true') fitAdd = a.fit === 'loose' ? 1 : 0;
      const raw = u + a.diff * 0.5 + chestAdj + hipAdj + thighAdj + fitAdd;
      let t = clamp(Math.round(raw + 1e-9), u - 1, u + 1); // halves round up, never more than one size away
      t = clamp(t, 0, SC.length - 1);
      let best = this.avail[0];
      this.avail.forEach(x => {
        const d = Math.abs(x.idx - t), bd = Math.abs(best.idx - t);
        if (d < bd || (d === bd && x.idx > best.idx)) best = x;
      });
      if (this.fit === 'small' && a.fit !== 'slim') notes.push('This style runs a little small, so we suggest sizing up one.');
      if (best.idx !== t) notes.push(SC[t] + ' is not offered for this style, so we picked the closest size available.');
      if (a.height < 158 || a.height > 175) notes.push('At ' + a.height + ' cm, <button type="button" class="rn-sf__inline" data-sf-chart-open>check the length in the size chart</button>.');
      return { key: best.key, idx: best.idx, notes };
    }

    onClick(e) {
      const o = e.target.closest('[data-a]');
      if (o) {
        if (this.busy) return;
        const a = o.dataset.a, v = o.dataset.v;
        const go = () => {
          this.busy = false; this.seen[a] = true;
          this.ans[a] = (a === 'diff') ? Number(v) : v;
          if (a === 'fit') this.rec = this.compute();
          this.step = this.steps.indexOf(a) + 1;
          this.draw();
        };
        if (calm()) { go(); return; }
        this.busy = true; o.classList.add('is-picked'); setTimeout(go, 170); return;
      }
      if (e.target.closest('[data-sf-next]')) { this.step = this.steps.indexOf('height') + 1; this.draw(); return; }
      if (e.target.closest('[data-sf-back]')) { this.step = Math.max(0, this.step - 1); this.draw(); return; }
      if (e.target.closest('[data-sf-redo]')) { this.reset(); return; }
      if (e.target.closest('[data-sf-chart-open]')) { if (this.rec) this.mark(this.rec.key); this.show('chart'); return; }
      if (e.target.closest('[data-sf-choose]')) this.choose();
    }

    choose() {
      const root = this.closest('.rnmp') || document;
      const inputs = [...root.querySelectorAll('.rnmp-option--size input[type="radio"]')];
      const target = inputs.find(i => norm(i.value) === this.rec.key);
      if (target) target.click();
      const dialog = this.closest('dialog');
      const close = dialog && dialog.querySelector('[data-rn-dialog-close]');
      if (close) close.click(); else if (dialog && dialog.close) dialog.close();
    }

    mark(key) {
      this.chart.querySelectorAll('tbody tr').forEach(tr => {
        const cell = tr.firstElementChild;
        tr.classList.toggle('is-yours', !!key && !!cell && norm(cell.textContent) === key);
      });
    }
  });
})();
